/**
 * Login V2 — SSO + Magic Link
 *
 * Offers Google SSO, Microsoft SSO, and email magic link.
 */

import { useState, useEffect, useRef, useCallback, KeyboardEvent } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useSession } from '@/features/auth/hooks/useSession'
import { env } from '@/lib/env'
import { safeParseJson, extractErrorMessage, formatAuthError, formatNetworkError, shouldShowConnectionScreen } from '@/lib/login-helpers'
import { useKeyboardShift } from './useKeyboardShift'
import { ConnectionErrorScreen } from '@/common/components/ConnectionErrorScreen'
import { XaviorLoader } from '@/common/components/XaviorLoader'

const AUTH_BASE_URL = env.authBaseUrl

// ---- SSO Error Messages ----

const SSO_ERROR_MESSAGES: Record<string, string> = {
  sso_unsupported: 'This sign-in method is not supported.',
  sso_denied: 'Sign-in was cancelled. Please try again.',
  sso_missing_params: 'Something went wrong with the sign-in. Please try again.',
  sso_invalid_state: 'Sign-in session expired. Please try again.',
  sso_expired: 'Sign-in session expired. Please try again.',
  sso_not_configured: 'This sign-in method is not available.',
  sso_no_email: 'Could not retrieve your email from the provider.',
  sso_account_suspended: 'This account has been suspended. Please contact support.',
  sso_access_denied: 'Access denied. Please contact support.',
  sso_failed: 'Sign-in failed. Please try again.',
}

// ---- SVG Icons ----

function GoogleIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844a4.14 4.14 0 01-1.796 2.716v2.259h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4" />
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.18l-2.908-2.259c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 009 18z" fill="#34A853" />
      <path d="M3.964 10.71A5.41 5.41 0 013.682 9c0-.593.102-1.17.282-1.71V4.958H.957A8.996 8.996 0 000 9c0 1.452.348 2.827.957 4.042l3.007-2.332z" fill="#FBBC05" />
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 00.957 4.958L3.964 7.29C4.672 5.163 6.656 3.58 9 3.58z" fill="#EA4335" />
    </svg>
  )
}

function MicrosoftIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
      <rect x="1" y="1" width="9" height="9" fill="#F25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7FBA00" />
      <rect x="1" y="11" width="9" height="9" fill="#00A4EF" />
      <rect x="11" y="11" width="9" height="9" fill="#FFB900" />
    </svg>
  )
}

// ---- Component ----

export function LoginV2() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { setToken, status } = useSession()
  const cardRef = useRef<HTMLDivElement>(null)
  const keyboardStyle = useKeyboardShift(cardRef)

  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [messageType, setMessageType] = useState<'success' | 'error' | ''>('')
  const [connectionError, setConnectionError] = useState<Error | null>(null)
  const [magicLinkSent, setMagicLinkSent] = useState(false)
  const [resendCountdown, setResendCountdown] = useState(0)
  const resendTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  // Show SSO error from redirect
  useEffect(() => {
    const ssoError = searchParams.get('error')
    if (ssoError && SSO_ERROR_MESSAGES[ssoError]) {
      setMessage(SSO_ERROR_MESSAGES[ssoError])
      setMessageType('error')
    }
  }, [searchParams])

  const startResendCountdown = useCallback(() => {
    setResendCountdown(60)
    if (resendTimerRef.current) clearInterval(resendTimerRef.current)
    resendTimerRef.current = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          if (resendTimerRef.current) clearInterval(resendTimerRef.current)
          resendTimerRef.current = null
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  useEffect(() => {
    return () => { if (resendTimerRef.current) clearInterval(resendTimerRef.current) }
  }, [])

  useEffect(() => {
    if (status === 'authenticated') {
      // Post-auth → app root (PDR 010). The gate chain (getMainContent → PlanGate
      // → name → app) decides what shows; there is no standalone landing page.
      navigate('/', { replace: true })
    }
  }, [status, navigate])

  const showMessage = (text: string, type: 'success' | 'error' | '' = '') => {
    setMessage(text)
    setMessageType(type)
  }

  const isValidEmail = (value: string): boolean => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
  }

  // ---- SSO Handler ----

  const handleSso = (provider: string) => {
    // The login provider is recorded server-side at the SSO callback
    // (users.login_provider, surfaced as profile.loginProvider, PDR 010 D3) — no
    // browser stash needed; the post-plan connect reads it to target the account.
    window.location.href = `${AUTH_BASE_URL}/auth/sso/${provider}`
  }

  // ---- Magic Link Handler ----

  const handleSendMagicLink = async () => {
    if (!email || !isValidEmail(email)) {
      showMessage('Please enter a valid email address (e.g. name@example.com)', 'error')
      return
    }

    setIsLoading(true)
    setConnectionError(null)

    try {
      const url = `${AUTH_BASE_URL}/auth/request-access`
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
        credentials: 'include',
      })

      const responseText = await response.text()
      const responseData = responseText ? safeParseJson(responseText) : null

      if (!response.ok) {
        if (shouldShowConnectionScreen(response.status)) {
          const detail = extractErrorMessage(responseData) || response.statusText || 'Login service unavailable'
          setConnectionError(new Error(`HTTP ${response.status}: ${detail}`))
          setIsLoading(false)
          return
        }
        throw new Error(formatAuthError(response.status, responseData))
      }

      if (!responseData || typeof responseData !== 'object') {
        throw new Error('Unexpected response from login service.')
      }

      const data = responseData as {
        autoLogin?: boolean
        accessToken?: string
        jwt?: string
        sessionToken?: string
        email?: string
        userId?: string
      }

      if (data.autoLogin && (data.accessToken || data.jwt || data.sessionToken)) {
        const authToken = data.accessToken || data.jwt || data.sessionToken
        sessionStorage.setItem('xavior_auth_token', authToken)
        sessionStorage.setItem('sessionToken', data.sessionToken)
        sessionStorage.setItem('userId', data.userId)
        sessionStorage.setItem('userEmail', data.email)
        setToken(authToken)
        setIsLoading(false)
        navigate('/', { replace: true })
        return
      }

      setMagicLinkSent(true)
      startResendCountdown()
      setIsLoading(false)

    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : String(error)
      console.error('[LoginV2] Error:', errorMessage)

      if (shouldShowConnectionScreen(undefined, error)) {
        const message = formatNetworkError(error)
        setConnectionError(error instanceof Error ? error : new Error(message))
        setIsLoading(false)
        return
      }
      showMessage(formatNetworkError(error), 'error')
      setIsLoading(false)
    }
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handleSendMagicLink()
  }

  // ---- Render: Connection Error ----

  if (connectionError) {
    return (
      <ConnectionErrorScreen
        error={connectionError}
        onRetry={() => {
          setConnectionError(null)
          if (email) void handleSendMagicLink()
        }}
      />
    )
  }

  // ---- Render: Checking Auth ----

  if (status === 'checking') {
    return (
      <div className="min-h-screen flex items-center justify-center p-5" style={{ background: '#f8f7f5' }}>
        <div className="bg-white rounded-2xl p-10 max-w-md w-full border" style={{ maxWidth: '420px', borderColor: '#e8e6e2' }}>
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="xavior-logo-mark w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0ea5e9' }}>
                <span className="text-white text-sm font-bold">X</span>
              </div>
              <span className="text-lg font-medium text-gray-900" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>XAVIOR</span>
            </div>
          </div>
          <div className="text-center">
            <XaviorLoader size="lg" className="mx-auto mb-4" />
            <p className="text-gray-400 text-sm">Restoring session...</p>
          </div>
        </div>
      </div>
    )
  }

  // ---- Render: Magic Link Sent ----

  if (magicLinkSent) {
    return (
      <div className="login-page min-h-[100dvh] flex items-center justify-center p-5 overflow-x-hidden transition-all duration-300" style={{ background: '#f8f7f5' }}>
        <div className="bg-white rounded-2xl p-10 max-w-md w-full border" style={{ maxWidth: '420px', borderColor: '#e8e6e2' }}>
          <div className="text-center mb-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="xavior-logo-mark w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0ea5e9' }}>
                <span className="text-white text-sm font-bold">X</span>
              </div>
              <span className="text-lg font-medium text-gray-900" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>XAVIOR</span>
            </div>
          </div>

          <div className="text-center mb-6">
            <div className="text-4xl mb-4">&#9993;</div>
            <h2 className="text-2xl font-normal text-gray-900 mb-2">Check your email</h2>
            <p className="text-gray-400 text-sm mb-1">We sent a magic link to</p>
            <p className="text-gray-900 font-medium text-sm">{email}</p>
          </div>

          <div className="rounded-xl border border-amber-100 bg-amber-50/60 px-4 py-3 text-center mb-6">
            <p className="text-amber-700 text-xs">Don't see it? Check your spam or promotions folder.</p>
          </div>

          <button
            onClick={() => {
              if (resendCountdown > 0) return
              void handleSendMagicLink()
              startResendCountdown()
            }}
            disabled={resendCountdown > 0 || isLoading}
            className="w-full py-3 px-6 bg-gray-50 hover:bg-gray-100 text-gray-600 font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-default text-sm border"
            style={{ borderColor: '#e8e6e2' }}
          >
            {resendCountdown > 0 ? `Resend in ${resendCountdown}s` : 'Resend magic link'}
          </button>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => { setMagicLinkSent(false); setMessage(''); setMessageType('') }}
              className="text-sm hover:underline bg-transparent border-none cursor-pointer"
              style={{ color: '#0ea5e9' }}
            >
              Use a different method
            </button>
          </div>
        </div>
      </div>
    )
  }

  // ---- Render: Main Login ----

  return (
    <div className="login-page min-h-[100dvh] flex items-center justify-center p-5 overflow-x-hidden transition-all duration-300" style={{ background: '#f8f7f5' }}>
      <div ref={cardRef} className="bg-white rounded-2xl p-10 max-w-md w-full border" style={{ maxWidth: '420px', borderColor: '#e8e6e2', ...keyboardStyle }}>

        {/* Logo */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-3 mb-3">
            <div className="xavior-logo-mark w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#0ea5e9' }}>
              <span className="text-white text-sm font-bold">X</span>
            </div>
            <span className="text-lg font-medium text-gray-900" style={{ letterSpacing: '0.12em', textTransform: 'uppercase' }}>XAVIOR</span>
          </div>
        </div>

        {/* Heading */}
        <div className="text-center mb-6">
          <h2 className="text-2xl font-normal text-gray-900 mb-2">Sign in or create account</h2>
          <p className="text-gray-400 text-sm">Choose your preferred sign-in method</p>
        </div>

        {/* SSO Buttons */}
        <button
          onClick={() => handleSso('google')}
          disabled={isLoading}
          className="w-full py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-xl text-sm border flex items-center justify-center gap-3 mb-3 disabled:opacity-50 disabled:cursor-default"
          style={{ borderColor: '#e8e6e2', transition: 'background 0.15s ease' }}
        >
          <GoogleIcon />
          Continue with Google
        </button>

        <button
          onClick={() => handleSso('microsoft')}
          disabled={isLoading}
          className="w-full py-3 px-4 bg-white hover:bg-gray-50 text-gray-700 font-medium rounded-xl text-sm border flex items-center justify-center gap-3 mb-6 disabled:opacity-50 disabled:cursor-default"
          style={{ borderColor: '#e8e6e2', transition: 'background 0.15s ease' }}
        >
          <MicrosoftIcon />
          Continue with Microsoft
        </button>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-6">
          <div className="flex-1 h-px" style={{ background: '#e8e6e2' }} />
          <span className="text-xs text-gray-400 uppercase tracking-wider">or</span>
          <div className="flex-1 h-px" style={{ background: '#e8e6e2' }} />
        </div>

        {/* Email Input */}
        <input
          type="email"
          name="email"
          id="email"
          autoComplete="email"
          enterKeyHint="send"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Enter your email"
          disabled={isLoading}
          className="w-full py-3.5 px-4 text-sm border rounded-xl mb-3 bg-white text-gray-900 focus:outline-none focus:ring-2 disabled:opacity-50"
          style={{ fontSize: '16px', borderColor: '#e8e6e2', transition: 'border-color 0.2s, box-shadow 0.2s' }}
          onFocus={(e) => { e.target.style.borderColor = '#0ea5e9'; e.target.style.boxShadow = '0 0 0 3px rgba(14,165,233,0.12)' }}
          onBlur={(e) => { e.target.style.borderColor = '#e8e6e2'; e.target.style.boxShadow = 'none' }}
        />

        <p className="text-gray-400 text-xs text-center mb-3">We'll send you a magic link — no password needed</p>

        <button
          onClick={handleSendMagicLink}
          disabled={isLoading}
          className="w-full py-3.5 px-6 text-white font-medium rounded-xl disabled:opacity-50 text-sm"
          style={{ background: '#0ea5e9', transition: 'background 0.15s ease' }}
          onMouseEnter={(e) => { (e.target as HTMLElement).style.background = '#0284c7' }}
          onMouseLeave={(e) => { (e.target as HTMLElement).style.background = '#0ea5e9' }}
        >
          {isLoading ? 'Sending...' : 'Continue with email'}
        </button>

        {/* Footer */}
        <div className="flex justify-center mt-5">
          <span className="text-xs text-gray-400">Free to start · No credit card required</span>
        </div>

        <div className="h-5 mt-3">
          {message && (
            <p className={`text-center text-sm ${
              messageType === 'success' ? 'text-[#0ea5e9]' :
              messageType === 'error' ? 'text-[var(--color-danger)]' :
              'text-gray-600'
            }`}>
              {message}
            </p>
          )}
        </div>
      </div>
    </div>
  )
}
